import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Alert,
  CircularProgress,
  Stack,
  TablePagination,
  MenuItem,
  Tabs,
  Tab,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import EventsApi from '../../api/eventsApi/eventsApi';
import { useSelector } from '../../redux/store';
import { UserResponse } from '../../api/eventsApi/eventsApi';
import InputAdornment from '@mui/material/InputAdornment';
import { reportUi } from '../reports/reportUiTheme';

const ROLE_CLAIMS = {
  Superadmin: '00000000-0000-0000-0000-000000000001',
  Admin: '00000000-0000-0000-0000-000000000002',
  User: '00000000-0000-0000-0000-000000000003',
} as const;

type RoleKey = keyof typeof ROLE_CLAIMS;

type OrganizationDto = {
  id: string;
  name: string;
};

const MANAGE_ORGS_VALUE = '__manage_orgs__';

/** Admin: tablo gövdesi / sayfalama için yumuşak zemin (başlık ve birincil butonlar reportUi) */
const adminUi = {
  rowBg: 'hsl(192 14% 98%)',
  rowHover: 'hsl(192 16% 95.5%)',
  paginationBg: 'hsl(192 18% 96%)',
  outlineHover: 'hsl(192 22% 91%)',
} as const;

const primaryButtonSx = {
  backgroundColor: reportUi.primary,
  color: '#fff',
  fontWeight: 600,
  borderRadius: reportUi.radius,
  boxShadow: 'none',
  '&:hover': {
    backgroundColor: reportUi.primaryHover,
    color: '#fff',
  },
} as const;

const secondaryButtonSx = {
  backgroundColor: reportUi.surface,
  color: reportUi.textMain,
  border: `1.5px solid ${reportUi.border}`,
  fontWeight: 600,
  borderRadius: reportUi.radius,
  boxShadow: 'none',
  '&:hover': {
    backgroundColor: reportUi.surfaceSoft,
    color: reportUi.textMain,
  },
} as const;

const dangerButtonSx = {
  backgroundColor: '#D93B2B',
  color: '#fff',
  fontWeight: 600,
  borderRadius: reportUi.radius,
  boxShadow: 'none',
  '&:hover': {
    backgroundColor: '#B82020',
    color: '#fff',
  },
} as const;

const tableHeadCellSx = {
  fontWeight: 700,
  backgroundColor: reportUi.headerBg,
  color: '#fff',
} as const;

const Admin: React.FC = () => {
  const userId = useSelector(state => state.auth.id);
  const [users, setUsers] = useState<UserResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [organizationId, setOrganizationId] = useState<string | null>(null);
  const [isSuperadmin, setIsSuperadmin] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [organizations, setOrganizations] = useState<OrganizationDto[]>([]);
  const [inviteOrganizationId, setInviteOrganizationId] = useState<string>('');
  const [orgDialogOpen, setOrgDialogOpen] = useState(false);
  const [newOrgName, setNewOrgName] = useState('');
  const [orgToDelete, setOrgToDelete] = useState<OrganizationDto | null>(null);
  const [confirmDeleteOrgOpen, setConfirmDeleteOrgOpen] = useState(false);
  const [editOrganizationId, setEditOrganizationId] = useState<string>('');

  // Pagination (max 5 per page)
  const [pageIndex, setPageIndex] = useState(0);
  const pageSize = 10;
  const [totalCount, setTotalCount] = useState(0);
  const [roleFilter, setRoleFilter] = useState<'All' | 'Admin' | 'User'>('User');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');

  // Form state
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [selectedRole, setSelectedRole] = useState<RoleKey>('User');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadUserData();
  }, [userId, pageIndex, roleFilter, search]);

  // Debounced search (email / firstName / lastName)
  useEffect(() => {
    const t = window.setTimeout(() => {
      setPageIndex(0);
      setSearch(searchInput.trim());
    }, 300);
    return () => window.clearTimeout(t);
  }, [searchInput]);

  const getUserRole = (u?: UserResponse): RoleKey => {
    const ids = u?.operationClaimIds || [];
    if (ids.includes(ROLE_CLAIMS.Superadmin)) return 'Superadmin';
    if (ids.includes(ROLE_CLAIMS.Admin)) return 'Admin';
    return 'User';
  };

  const loadUserData = async () => {
    if (!userId) return;

    try {
      setLoading(true);
      setError(null);

      // Get current user to get organizationId (for adding new users)
      const currentUser = await EventsApi.getUser();
      setOrganizationId(currentUser.organizationId);

      const currentRole = getUserRole(currentUser);
      setIsSuperadmin(currentRole === 'Superadmin');
      setIsAdmin(currentRole === 'Admin');

      if (currentRole === 'Superadmin') {
        const orgList = await EventsApi.getOrganizations(0, 1000);
        const items: OrganizationDto[] = (orgList.items || []).slice().sort((a: any, b: any) => {
          const an = (a?.name || '').toString();
          const bn = (b?.name || '').toString();
          return an.localeCompare(bn, 'tr');
        });
        setOrganizations(items);
        if (!inviteOrganizationId && items.length > 0) setInviteOrganizationId(items[0].id);
      } else {
        setOrganizations([]);
        setInviteOrganizationId('');
      }

      // Non-superadmin users should never be able to switch away from User list
      if (currentRole !== 'Superadmin' && roleFilter !== 'User') {
        setRoleFilter('User');
        setPageIndex(0);
      }

      // Backend already enforces:
      // - Superadmin: Admin + User list (excludes Superadmins)
      // - Admin: only Users in same organization
      const effectiveFilter = currentRole === 'Superadmin' ? roleFilter : 'User';
      const usersList = await EventsApi.getUsers(pageIndex, pageSize, effectiveFilter, search);
      setUsers(usersList.items || []);
      setTotalCount(usersList.count ?? 0);
    } catch (err: any) {
      setError(err.message || 'Kullanıcılar yüklenirken bir hata oluştu.');
      console.error('Error loading users:', err);
      setUsers([]); // Ensure users is always an array
    } finally {
      setLoading(false);
    }
  };

  const handleAddUser = async () => {
    const orgIdToUse = isSuperadmin ? inviteOrganizationId : (organizationId || '');
    if (!orgIdToUse) {
      setError(isSuperadmin ? 'Lütfen organizasyon seçin.' : 'Organizasyon bilgisi bulunamadı.');
      return;
    }

    // Validation
    if (!firstName.trim() || !lastName.trim() || !email.trim()) {
      setError('Lütfen tüm alanları doldurun.');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);
      setSuccess(null);

      const created = await EventsApi.registerUser({
        email: email.trim(),
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        organizationId: orgIdToUse,
        operationClaimId: (isSuperadmin || isAdmin) ? ROLE_CLAIMS[selectedRole] : ROLE_CLAIMS.User,
      });

      // If Superadmin selected Admin role, promote after creation (in case backend assigns default User)
      if (isSuperadmin && selectedRole === 'Admin' && created?.id) {
        await EventsApi.setUserRole(created.id, ROLE_CLAIMS.Admin);
      }

      setSuccess('Kullanıcı başarıyla eklendi.');
      setAddDialogOpen(false);
      resetForm();
      await loadUserData();
    } catch (err: any) {
      setError(err.message || 'Kullanıcı eklenirken bir hata oluştu.');
      console.error('Error adding user:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateUser = async () => {
    if (!selectedUserId) {
      setError('Kullanıcı bilgisi bulunamadı.');
      return;
    }
    if (!editOrganizationId) {
      setError('Lütfen organizasyon seçin.');
      return;
    }

    // Validation
    if (!firstName.trim() || !lastName.trim() || !email.trim()) {
      setError('Lütfen ad, soyad ve e-posta alanlarını doldurun.');
      return;
    }


    try {
      setSubmitting(true);
      setError(null);
      setSuccess(null);

      // Superadmin can change role (Admin/User) for the selected user
      if (isSuperadmin) {
        const user = users.find(u => u.id === selectedUserId);
        if (user) {
          const currentRole = getUserRole(user);
          // Do not attempt to modify superadmins from UI (backend also blocks)
          if (currentRole !== 'Superadmin' && currentRole !== selectedRole) {
            await EventsApi.setUserRole(selectedUserId, ROLE_CLAIMS[selectedRole]);
          }
        }
      }

      const updateData: any = {
        id: selectedUserId,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim(),
        organizationId: editOrganizationId,
      };

      await EventsApi.updateUser(updateData);

      setSuccess('Kullanıcı başarıyla güncellendi.');
      setEditDialogOpen(false);
      resetForm();
      setSelectedUserId(null);
      await loadUserData();
    } catch (err: any) {
      setError(err.message || 'Kullanıcı güncellenirken bir hata oluştu.');
      console.error('Error updating user:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUserId) return;

    try {
      setSubmitting(true);
      setError(null);
      setSuccess(null);

      await EventsApi.deleteUser(selectedUserId);

      setSuccess('Kullanıcı başarıyla silindi.');
      setDeleteDialogOpen(false);
      setSelectedUserId(null);
      await loadUserData();
    } catch (err: any) {
      setError(err.message || 'Kullanıcı silinirken bir hata oluştu.');
      console.error('Error deleting user:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setFirstName('');
    setLastName('');
    setEmail('');
    setSelectedRole('User');
  };

  const openEditDialog = (user: UserResponse) => {
    if (!isSuperadmin) return;
    setSelectedUserId(user.id);
    setFirstName(user.firstName);
    setLastName(user.lastName);
    setEmail(user.email);
    const role = getUserRole(user);
    setSelectedRole(role === 'Superadmin' ? 'User' : role);
    setEditOrganizationId((user as any).organizationId || '');
    setEditDialogOpen(true);
  };

  const openDeleteDialog = (userId: string) => {
    if (!isSuperadmin) return;
    setSelectedUserId(userId);
    setDeleteDialogOpen(true);
  };

  const closeAddDialog = () => {
    if (!submitting) {
      setAddDialogOpen(false);
      resetForm();
      setError(null);
    }
  };

  const closeEditDialog = () => {
    if (!submitting) {
      setEditDialogOpen(false);
      resetForm();
      setSelectedUserId(null);
      setEditOrganizationId('');
      setError(null);
    }
  };

  const closeDeleteDialog = () => {
    if (!submitting) {
      setDeleteDialogOpen(false);
      setSelectedUserId(null);
      setError(null);
    }
  };

  const openOrgDialog = () => {
    setNewOrgName('');
    setOrgDialogOpen(true);
  };

  const closeOrgDialog = () => {
    if (!submitting) {
      setOrgDialogOpen(false);
      setNewOrgName('');
    }
  };

  const reloadOrganizations = async (selectId?: string) => {
    const orgList = await EventsApi.getOrganizations(0, 1000);
    const items: OrganizationDto[] = (orgList.items || []).slice().sort((a: any, b: any) => {
      const an = (a?.name || '').toString();
      const bn = (b?.name || '').toString();
      return an.localeCompare(bn, 'tr');
    });
    setOrganizations(items);
    if (selectId) setInviteOrganizationId(selectId);
    else if (items.length > 0 && !items.some(o => o.id === inviteOrganizationId)) setInviteOrganizationId(items[0].id);
  };

  const handleCreateOrganization = async () => {
    const name = newOrgName.trim();
    if (!name) {
      setError('Organizasyon adı boş olamaz.');
      return;
    }
    try {
      setSubmitting(true);
      setError(null);
      const created = await EventsApi.createOrganization({ name });
      await reloadOrganizations(created?.id);
      closeOrgDialog();
      setSuccess('Organizasyon oluşturuldu.');
    } catch (err: any) {
      setError(err.message || 'Organizasyon oluşturulurken hata oluştu.');
    } finally {
      setSubmitting(false);
    }
  };

  const requestDeleteOrganization = (org: OrganizationDto) => {
    setOrgToDelete(org);
    setConfirmDeleteOrgOpen(true);
  };

  const closeConfirmDeleteOrg = () => {
    if (!submitting) {
      setConfirmDeleteOrgOpen(false);
      setOrgToDelete(null);
    }
  };

  const confirmDeleteOrganization = async () => {
    if (!orgToDelete) return;
    try {
      setSubmitting(true);
      setError(null);
      await EventsApi.deleteOrganization(orgToDelete.id);
      await reloadOrganizations();
      setSuccess('Organizasyon silindi.');
    } catch (err: any) {
      setError(err.message || 'Organizasyon silinirken hata oluştu.');
    } finally {
      setSubmitting(false);
      closeConfirmDeleteOrg();
    }
  };

  const getOrganizationName = (orgId?: string | null) => {
    if (!orgId) return '-';
    return organizations.find(o => o.id === orgId)?.name ?? '-';
  };

  const selectedUser = Array.isArray(users) ? users.find(u => u.id === selectedUserId) : undefined;

  return (
    <Box sx={{ p: 3, minHeight: '100vh', bgcolor: reportUi.pageBg }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="overline" sx={{ color: reportUi.textMuted, letterSpacing: 2 }}>
            YÖNETİM
          </Typography>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 700, color: reportUi.textMain }}>
            Admin Paneli
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <TextField
            size="small"
            label="Ara (Ad, Soyad, E-posta)"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              ),
            }}
            sx={{
              minWidth: 320,
              '& .MuiOutlinedInput-root': {
                bgcolor: reportUi.surface,
                borderRadius: reportUi.radius,
                '& fieldset': { borderColor: reportUi.border },
                '&:hover fieldset': { borderColor: reportUi.primary },
                '&.Mui-focused fieldset': { borderColor: reportUi.primary },
              },
            }}
          />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setAddDialogOpen(true)}
            sx={{
              py: 1.5,
              ...primaryButtonSx,
            }}
          >
            Yeni Kullanıcı Ekle
          </Button>
        </Box>
      </Box>

      {isSuperadmin && (
        <Tabs
          value={roleFilter}
          onChange={(_, v) => {
            // Always force a refresh even if we're already on page 0
            setUsers([]);
            setTotalCount(0);
            setPageIndex(0);
            setRoleFilter(v);
          }}
          sx={{
            mb: 2,
            '& .MuiTabs-indicator': { backgroundColor: reportUi.primary },
            '& .MuiTab-root': { color: reportUi.textMuted, fontWeight: 700 },
            '& .Mui-selected': { color: reportUi.textMain },
          }}
        >
          <Tab value="User" label="Kullanıcılar" />
          <Tab value="Admin" label="Adminler" />
          <Tab value="All" label="Tümü" />
        </Tabs>
      )}

      {error && (
        <Alert
          severity="error"
          variant="outlined"
          sx={{
            mb: 2,
            borderRadius: reportUi.radius,
            backgroundColor: reportUi.surface,
            borderColor: '#D93B2B',
          }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      {success && (
        <Alert
          severity="success"
          variant="outlined"
          sx={{
            mb: 2,
            borderRadius: reportUi.radius,
            backgroundColor: reportUi.surface,
            borderColor: '#1FA35C',
          }}
          onClose={() => setSuccess(null)}
        >
          {success}
        </Alert>
      )}

      <Paper sx={{ width: '100%', overflow: 'hidden', bgcolor: adminUi.paginationBg, border: `1px solid ${reportUi.border}`, borderRadius: reportUi.radius, boxShadow: 'none' }}>
        <TableContainer sx={{ maxHeight: 'calc(100vh - 300px)', bgcolor: adminUi.rowBg }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4, bgcolor: adminUi.rowBg }}>
              <CircularProgress sx={{ color: reportUi.primary }} />
            </Box>
          ) : (
            <Table stickyHeader sx={{ bgcolor: adminUi.rowBg }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={tableHeadCellSx}>
                    Ad
                  </TableCell>
                  <TableCell sx={tableHeadCellSx}>
                    Soyad
                  </TableCell>
                  <TableCell sx={tableHeadCellSx}>
                    E-posta
                  </TableCell>
                  {isSuperadmin && (
                    <TableCell sx={tableHeadCellSx}>
                      Organizasyon
                    </TableCell>
                  )}
                  {isSuperadmin && (
                    <TableCell sx={tableHeadCellSx}>
                      Rol
                    </TableCell>
                  )}
                  {isSuperadmin && (
                    <TableCell sx={{ ...tableHeadCellSx, width: 200 }}>
                      İşlemler
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {!Array.isArray(users) || users.length === 0 ? (
                  <TableRow sx={{ bgcolor: adminUi.rowBg }}>
                    <TableCell colSpan={isSuperadmin ? 6 : 3} align="center" sx={{ py: 4, bgcolor: adminUi.rowBg, border: 'none' }}>
                      <Typography color="text.secondary">
                        Henüz kullanıcı bulunmamaktadır.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  users.map((user) => (
                    <TableRow
                      key={user.id}
                      hover
                      sx={{
                        bgcolor: adminUi.rowBg,
                        '&:hover': { backgroundColor: adminUi.rowHover },
                      }}
                    >
                      <TableCell>{user.firstName}</TableCell>
                      <TableCell>{user.lastName}</TableCell>
                      <TableCell>{user.email}</TableCell>
                      {isSuperadmin && (
                        <TableCell>{getOrganizationName((user as any).organizationId)}</TableCell>
                      )}
                      {isSuperadmin && (
                        <TableCell>{getUserRole(user)}</TableCell>
                      )}
                      {isSuperadmin && (
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button
                              variant="contained"
                              startIcon={<EditIcon />}
                              onClick={() => openEditDialog(user)}
                              size="small"
                              sx={{
                                px: 1.5,
                                py: 0.75,
                                minWidth: 'auto',
                                ...primaryButtonSx,
                              }}
                            >
                              Düzenle
                            </Button>
                            <Button
                              variant="contained"
                              startIcon={<DeleteIcon />}
                              onClick={() => openDeleteDialog(user.id)}
                              size="small"
                              sx={{
                                px: 1.5,
                                py: 0.75,
                                minWidth: 'auto',
                                ...dangerButtonSx,
                              }}
                            >
                              Sil
                            </Button>
                          </Box>
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </TableContainer>
        <TablePagination
          component="div"
          count={totalCount}
          page={pageIndex}
          onPageChange={(_, newPage) => setPageIndex(newPage)}
          rowsPerPage={pageSize}
          rowsPerPageOptions={[pageSize]}
          sx={{
            borderTop: `1px solid ${reportUi.border}`,
            bgcolor: adminUi.paginationBg,
            color: reportUi.textMain,
            '& .MuiTablePagination-toolbar': {
              bgcolor: adminUi.paginationBg,
              minHeight: 48,
            },
            '& .MuiTablePagination-selectIcon': { color: reportUi.textMuted },
            '& .MuiIconButton-root': { color: reportUi.textMain },
            '& .Mui-disabled': { opacity: 0.45 },
            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
              color: reportUi.textMuted,
              fontWeight: 500,
            },
          }}
        />
      </Paper>

      {/* Add User Dialog */}
      <Dialog open={addDialogOpen} onClose={closeAddDialog} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: reportUi.radius, bgcolor: reportUi.pageBg, border: `1px solid ${reportUi.border}` } }}>
        <DialogTitle sx={{ m: 0, p: 2, paddingRight: '48px', bgcolor: reportUi.headerBg, color: '#fff' }}>
          Yeni Kullanıcı Ekle
          <IconButton
            aria-label="close"
            onClick={closeAddDialog}
            disabled={submitting}
            sx={{ position: 'absolute', right: 8, top: 8, color: '#fff' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Ad"
              fullWidth
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              disabled={submitting}
              required
            />
            <TextField
              label="Soyad"
              fullWidth
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              disabled={submitting}
              required
            />
            <TextField
              label="E-posta"
              fullWidth
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={submitting}
              required
            />
            {(isSuperadmin || isAdmin) && (
              isSuperadmin ? (
                <>
                  <TextField
                    label="Organizasyon"
                    select
                    fullWidth
                    value={inviteOrganizationId}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v === MANAGE_ORGS_VALUE) {
                        openOrgDialog();
                        return;
                      }
                      setInviteOrganizationId(v);
                    }}
                    disabled={submitting}
                    required
                    helperText="Davet edilecek kişinin organizasyonunu seçin."
                  >
                    {organizations.map((o) => (
                      <MenuItem key={o.id} value={o.id}>
                        {o.name}
                      </MenuItem>
                    ))}
                    <MenuItem value={MANAGE_ORGS_VALUE}>Organizasyonları düzenle</MenuItem>
                  </TextField>
                </>
              ) : (
                <TextField
                  label="Organizasyon"
                  fullWidth
                  value="Kendi organizasyonunuz (otomatik)"
                  disabled
                  helperText="Admin kullanıcılar ekledikleri kişiyi otomatik kendi organizasyonuna ekler."
                />
              )
            )}
            <TextField
              label="Şifre"
              fullWidth
              value="(Sistem tarafından otomatik oluşturulacak)"
              disabled
              helperText="Davet edilen kullanıcıya geçici şifre e-posta ile iletilir."
            />
            {(isSuperadmin || isAdmin) && (
              <TextField
                label="Yetki"
                select
                fullWidth
                value={selectedRole}
                onChange={(e) => setSelectedRole(e.target.value as RoleKey)}
                disabled={submitting}
              >
                <MenuItem value="User">User</MenuItem>
                <MenuItem value="Admin">Admin</MenuItem>
              </TextField>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={closeAddDialog}
            disabled={submitting}
            variant="contained"
            sx={secondaryButtonSx}
          >
            İptal
          </Button>
          <Button
            onClick={handleAddUser}
            variant="contained"
            disabled={submitting}
            sx={primaryButtonSx}
          >
            {submitting ? <CircularProgress size={20} /> : 'Ekle'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Organization Dialog (Superadmin) */}
      {isSuperadmin && (
        <Dialog open={orgDialogOpen} onClose={closeOrgDialog} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: reportUi.radius, bgcolor: reportUi.pageBg, border: `1px solid ${reportUi.border}` } }}>
          <DialogTitle sx={{ m: 0, p: 2, paddingRight: '48px', bgcolor: reportUi.headerBg, color: '#fff' }}>
            Organizasyonları Düzenle
            <IconButton
              aria-label="close"
              onClick={closeOrgDialog}
              disabled={submitting}
              sx={{ position: 'absolute', right: 8, top: 8, color: '#fff' }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Typography sx={{ fontWeight: 700, color: reportUi.textMain }}>
                Organizasyonlar
              </Typography>
              <Box sx={{ border: `1px solid ${reportUi.border}`, borderRadius: reportUi.radius, bgcolor: reportUi.surface }}>
                {organizations.length === 0 ? (
                  <Box sx={{ p: 2 }}>
                    <Typography color="text.secondary">
                      Henüz organizasyon yok.
                    </Typography>
                  </Box>
                ) : (
                  organizations.map((o, idx) => (
                    <Box
                      key={o.id}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 2,
                        p: 1.5,
                        borderBottom: idx === organizations.length - 1 ? 'none' : '1px solid #eee',
                      }}
                    >
                      <Typography sx={{ fontWeight: 600 }}>{o.name}</Typography>
                      <Button
                        onClick={() => requestDeleteOrganization(o)}
                        disabled={submitting}
                        variant="contained"
                        startIcon={<DeleteIcon />}
                        sx={{
                          ...dangerButtonSx,
                        }}
                      >
                        Sil
                      </Button>
                    </Box>
                  ))
                )}
              </Box>

              <Typography sx={{ fontWeight: 700, color: reportUi.textMain, mt: 1 }}>
                Yeni Organizasyon Ekle
              </Typography>
              <TextField
                label="Organizasyon Adı"
                fullWidth
                value={newOrgName}
                onChange={(e) => setNewOrgName(e.target.value)}
                disabled={submitting}
                required
              />
            </Stack>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button
              onClick={closeOrgDialog}
              disabled={submitting}
              variant="contained"
              sx={secondaryButtonSx}
            >
              İptal
            </Button>
            <Button
              onClick={handleCreateOrganization}
              disabled={submitting}
              variant="contained"
              sx={primaryButtonSx}
            >
              {submitting ? <CircularProgress size={20} /> : 'Ekle'}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Confirm Delete Organization */}
      {isSuperadmin && (
        <Dialog open={confirmDeleteOrgOpen} onClose={closeConfirmDeleteOrg} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: reportUi.radius, bgcolor: reportUi.pageBg, border: `1px solid ${reportUi.border}` } }}>
          <DialogTitle sx={{ m: 0, p: 2, paddingRight: '48px', bgcolor: reportUi.headerBg, color: '#fff' }}>
            Organizasyonu Sil
            <IconButton
              aria-label="close"
              onClick={closeConfirmDeleteOrg}
              disabled={submitting}
              sx={{ position: 'absolute', right: 8, top: 8, color: '#fff' }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <Typography sx={{ mt: 1 }}>
              <strong>{orgToDelete?.name ?? 'Seçili organizasyon'}</strong> silinsin mi?
              <br />
              <br />
              Bu işlem <strong>geri alınamaz</strong>.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button
              onClick={closeConfirmDeleteOrg}
              disabled={submitting}
              variant="contained"
              sx={{
                backgroundColor: '#fff',
                color: reportUi.primary,
                border: `1.5px solid ${reportUi.primary}`,
                fontWeight: 600,
                borderRadius: '8px',
                boxShadow: 'none',
                '&:hover': { backgroundColor: adminUi.outlineHover, color: reportUi.primary },
              }}
            >
              Vazgeç
            </Button>
            <Button
              onClick={confirmDeleteOrganization}
              disabled={submitting || !orgToDelete}
              variant="contained"
              sx={{
                backgroundColor: '#d32f2f',
                color: '#fff',
                fontWeight: 600,
                borderRadius: '8px',
                boxShadow: 'none',
                '&:hover': { backgroundColor: '#b71c1c', color: '#fff' },
              }}
            >
              {submitting ? <CircularProgress size={20} /> : 'Sil'}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Edit User Dialog */}
      <Dialog open={editDialogOpen} onClose={closeEditDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ m: 0, p: 2, paddingRight: '48px' }}>
          Kullanıcı Düzenle
          <IconButton
            aria-label="close"
            onClick={closeEditDialog}
            disabled={submitting}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Ad"
              fullWidth
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              disabled={submitting}
              required
            />
            <TextField
              label="Soyad"
              fullWidth
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              disabled={submitting}
              required
            />
            <TextField
              label="E-posta"
              fullWidth
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={submitting}
              required
            />
            {isSuperadmin && (
              <TextField
                label="Organizasyon"
                select
                fullWidth
                value={editOrganizationId}
                onChange={(e) => setEditOrganizationId(e.target.value)}
                disabled={submitting}
                required
              >
                {organizations.map((o) => (
                  <MenuItem key={o.id} value={o.id}>
                    {o.name}
                  </MenuItem>
                ))}
              </TextField>
            )}
            <TextField
              label="Şifre"
              fullWidth
              value="(Bu ekrandan değiştirilemez)"
              disabled
              helperText="Şifre değişikliği kullanıcı tarafından 'Şifre Değiştir' alanından yapılmalıdır."
            />
            {isSuperadmin && (
              <TextField
                label="Yetki"
                select
                fullWidth
                value={selectedRole}
                onChange={(e) => setSelectedRole(e.target.value as RoleKey)}
                disabled={submitting || getUserRole(selectedUser) === 'Superadmin'}
                helperText={getUserRole(selectedUser) === 'Superadmin' ? 'Superadmin yetkisi bu ekrandan değiştirilemez.' : undefined}
              >
                <MenuItem value="User">User</MenuItem>
                <MenuItem value="Admin">Admin</MenuItem>
              </TextField>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={closeEditDialog}
            disabled={submitting}
            variant="contained"
            sx={{
              backgroundColor: '#fff',
              color: reportUi.primary,
              border: `1.5px solid ${reportUi.primary}`,
              fontWeight: 600,
              borderRadius: '8px',
              boxShadow: 'none',
              '&:hover': {
                backgroundColor: adminUi.outlineHover,
                color: reportUi.primary,
              },
            }}
          >
            İptal
          </Button>
          <Button
            onClick={handleUpdateUser}
            variant="contained"
            disabled={submitting}
            sx={{
              backgroundColor: reportUi.primary,
              color: '#fff',
              fontWeight: 600,
              borderRadius: '8px',
              boxShadow: 'none',
              '&:hover': {
                backgroundColor: reportUi.primaryHover,
                color: '#fff',
              },
            }}
          >
            {submitting ? <CircularProgress size={20} /> : 'Güncelle'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete User Dialog */}
      <Dialog open={deleteDialogOpen} onClose={closeDeleteDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ m: 0, p: 2, paddingRight: '48px' }}>
          Kullanıcıyı Sil
          <IconButton
            aria-label="close"
            onClick={closeDeleteDialog}
            disabled={submitting}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography>
            {selectedUser && (
              <>
                <strong>{selectedUser.firstName} {selectedUser.lastName}</strong> ({selectedUser.email}) kullanıcısını silmek istediğinize emin misiniz?
                <br />
                <br />
                Bu işlem geri alınamaz.
              </>
            )}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={closeDeleteDialog}
            disabled={submitting}
            variant="contained"
            sx={{
              backgroundColor: '#fff',
              color: reportUi.primary,
              border: `1.5px solid ${reportUi.primary}`,
              fontWeight: 600,
              borderRadius: '8px',
              boxShadow: 'none',
              '&:hover': {
                backgroundColor: adminUi.outlineHover,
                color: reportUi.primary,
              },
            }}
          >
            İptal
          </Button>
          <Button
            onClick={handleDeleteUser}
            variant="contained"
            disabled={submitting}
            sx={{
              backgroundColor: '#d32f2f',
              color: '#fff',
              fontWeight: 600,
              borderRadius: '8px',
              boxShadow: 'none',
              '&:hover': {
                backgroundColor: '#b71c1c',
                color: '#fff',
              },
            }}
          >
            {submitting ? <CircularProgress size={20} /> : 'Sil'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Admin;

